<script setup lang="ts">
import { useIntervalFn, useResizeObserver } from '@vueuse/core'
import { useTheme } from 'vuetify'
import { useAiStudyChatStore } from '@/views/games/ai-study-chat/useAiStudyChatStore'
import type { UiMessage } from '@/views/games/ai-study-chat/useAiStudyChatStore'
import AiStudyChatSidebar from '@/views/games/ai-study-chat/AiStudyChatSidebar.vue'
import AiStudyChatLog from '@/views/games/ai-study-chat/AiStudyChatLog.vue'
import AiStudyChatComposer from '@/views/games/ai-study-chat/AiStudyChatComposer.vue'
import AiStudyChatEmpty from '@/views/games/ai-study-chat/AiStudyChatEmpty.vue'
import NewConversationDialog from '@/views/games/ai-study-chat/NewConversationDialog.vue'
import { AI_CHAT_LOW_QUOTA_THRESHOLD } from '@/utils/constants'
import { Modules } from '@/types/generated'

definePage({
  meta: {
    action: 'read',
    subject: 'games',
    layoutWrapperClasses: 'layout-content-height-fixed',
  },
})

const { t } = useI18n()
const router = useRouter()
const theme = useTheme()

// Entitlement guard (mirrors the knowledge-base feature-flag guard)
const orgSettings = useCookie<any>('organizationSettings')
if (!(orgSettings.value?.subscriptionFeatures ?? []).includes('ai_chat_messages'))
  await router.replace('/games/scenarios/list')

const userData = useCookie<any>('userData')
const store = useAiStudyChatStore()

const Status = Modules.AiChat.Enums.ChatMessageStatus
const Role = Modules.AiChat.Enums.ChatRole

const msg = ref('')
const isNewDialogVisible = ref(false)

// Мобильный drawer списка бесед (≤940px сайдбар скрыт за off-canvas).
const isSidebarOpen = ref(false)

const isSnackbarVisible = ref(false)
const snackbarText = ref('')

const isDark = computed(() => theme.global.current.value.dark)

const userInitials = computed(() => {
  const name = (userData.value?.fullName || userData.value?.email || '?').trim()

  return name.split(/\s+/).map((p: string) => p[0]).slice(0, 2).join('').toUpperCase()
})

const contextLabel = computed(() =>
  store.activeConversation ? store.titleFor(store.activeConversation) || null : null)

// Empty-state mode: no active conversation → welcome; active but no messages → suggestions.
const emptyMode = computed<'welcome' | 'suggestions'>(() => store.activeId ? 'suggestions' : 'welcome')

const notifyError = (text: string) => {
  snackbarText.value = text
  isSnackbarVisible.value = true
}

const quotaBlocked = computed(() => {
  const q = store.quota
  if (!q)
    return false

  return q.available === false || q.remaining === 0
})

const showQuotaBadge = computed(() => {
  const q = store.quota
  if (!q || q.remaining == null)
    return false

  return q.remaining <= AI_CHAT_LOW_QUOTA_THRESHOLD
})

const scrollLog = ref<HTMLElement | null>(null)
const logRef = ref<any>(null)

// "Stick to bottom": follow new content (an answer arriving over WS, thinking-stage
// height changes) only when the user is already near the bottom — so scrolling up to
// read history is never hijacked. `stick` is updated on user scroll; growing the content
// doesn't fire a scroll event, so the flag keeps the user's last intent.
const stick = ref(true)

const onThreadScroll = () => {
  const el = scrollLog.value
  if (el)
    stick.value = el.scrollHeight - el.scrollTop - el.clientHeight < 120
}

const scrollToBottom = () => nextTick(() => {
  if (scrollLog.value)
    scrollLog.value.scrollTop = scrollLog.value.scrollHeight
})

useResizeObserver(logRef, () => {
  if (stick.value)
    scrollToBottom()
})

// When the assistant placeholder flips pending → ready, scroll to the TOP of that
// answer (not the bottom) so the reader starts at the first line of the reply.
const pendingAssistantId = computed(() =>
  store.messages.find(m => m.role === Role.Assistant && m.status === Status.Pending)?.id ?? null)

watch(pendingAssistantId, (now, prev) => {
  if (!prev || now)
    return
  const m = store.messages.find(x => x.id === prev)
  if (!m || m.status !== Status.Ready)
    return // failed, or switched conversation (id gone) — keep default behaviour
  stick.value = false // otherwise the resize-observer drags us back to the bottom
  nextTick(() => document.getElementById(`chat-msg-${prev}`)?.scrollIntoView({ block: 'start', behavior: 'smooth' }))
})

const onSelect = async (id: string) => {
  isSidebarOpen.value = false
  await store.fetchMessages(id)
  scrollToBottom()
}

const onStarted = async (id: string) => {
  await store.fetchMessages(id)
  scrollToBottom()
}

const onNewFromSidebar = () => {
  isSidebarOpen.value = false
  isNewDialogVisible.value = true
}

// Крошка «AI-чат обучения» → welcome-экран (там своя кнопка «Новая беседа» —
// на мобилке это единственный путь начать беседу без drawer'а).
const goHome = () => {
  isSidebarOpen.value = false
  store.deselectConversation()
}

const handleResult = async (result: { ok: boolean; status?: number; code?: string }) => {
  scrollToBottom()
  if (result.ok)
    return
  if (result.code === 'provider_unsupported') {
    notifyError(t('ai-study-chat-page.errors.provider_unsupported'))
  }
  else if (result.status === 429) {
    notifyError(t('ai-study-chat-page.errors.rate_limit'))
  }
  else if (result.status === 402) {
    notifyError(t('ai-study-chat-page.errors.quota'))
    await store.fetchQuota()
  }
  else {
    notifyError(t('ai-study-chat-page.errors.send'))
  }
}

const send = async () => {
  const text = msg.value.trim()
  if (!text || store.isAwaitingReply || quotaBlocked.value || !store.activeId)
    return

  msg.value = ''
  scrollToBottom()
  await handleResult(await store.sendMessage(text))
}

// A suggestion tile sends its title as the first question.
const onSuggest = async (text: string) => {
  if (!store.activeId || store.isAwaitingReply || quotaBlocked.value)
    return
  scrollToBottom()
  await handleResult(await store.sendMessage(text))
}

// Failed bubble → resend its text (store drops the failed bubble first).
const onRetry = async (m: UiMessage) => {
  if (store.isAwaitingReply || quotaBlocked.value)
    return
  await handleResult(await store.retryMessage(m))
}

// Hydrate sidebar fallback titles for conversations whose server title is still
// null (survives reload). Only fetch the lists if at least one conversation needs it.
const loadSourceTitles = async () => {
  if (!store.conversations.some(c => !c.title))
    return
  const roles: string[] = userData.value?.roles ?? []
  const isAdminOrManager = roles.some(r => ['admin', 'scoped_admin', 'manager'].includes(r))

  const [courses, scenarios] = await Promise.all([
    $api<any[]>('/courses').catch(() => []),
    $api<any[]>(isAdminOrManager ? '/scenarios/all-for-ai' : '/scenarios/all-public-for-ai').catch(() => []),
  ])

  const map: Record<string, string> = {}
  for (const c of courses ?? [])
    map[c.id] = c.title
  for (const s of scenarios ?? [])
    map[s.id] = s.title
  store.setTargetTitles(map)
}

useIntervalFn(async () => {
  if (store.isAwaitingReply)
    await store.reconcileActive()
}, 60000)

onMounted(async () => {
  await Promise.all([store.fetchConversations(), store.fetchQuota()])
  await loadSourceTitles()

  if (userData.value?.id)
    store.subscribe(userData.value.id)
})

onUnmounted(() => {
  if (userData.value?.id)
    store.unsubscribe(userData.value.id)
})
</script>

<template>
  <div
    class="ai-study-chat-root"
    :class="[{ 'is-dark': isDark }]"
  >
    <div class="app-shell">
      <header class="topbar">
        <button
          class="sidebar-toggle"
          :aria-label="t('menu')"
          @click="isSidebarOpen = !isSidebarOpen"
        >
          <VIcon
            icon="tabler-menu-2"
            size="20"
          />
        </button>
        <div class="crumb">
          <button
            class="crumb-home"
            @click="goHome"
          >
            <VIcon
              icon="tabler-robot"
              size="18"
            />
            <b>{{ t('ai-study-chat-page.title') }}</b>
          </button>
          <template v-if="contextLabel">
            <VIcon
              icon="tabler-chevron-right"
              size="13"
              class="sep"
            />
            <span>{{ contextLabel }}</span>
          </template>
        </div>
        <VSpacer />
        <VChip
          v-if="showQuotaBadge"
          color="warning"
          size="small"
          label
        >
          {{ t('ai-study-chat-page.quota.remaining', { count: store.quota?.remaining }) }}
        </VChip>
      </header>

      <div
        class="workspace"
        :class="[{ 'sidebar-open': isSidebarOpen }]"
      >
        <div
          v-if="isSidebarOpen"
          class="sidebar-scrim"
          @click="isSidebarOpen = false"
        />
        <AiStudyChatSidebar
          @new="onNewFromSidebar"
          @select="onSelect"
        />

        <div class="chat">
          <!--
            Native scroll (matches the prototype). PerfectScrollbar was avoided here on purpose:
            it sets overflow:hidden and only recalcs in its own updated() hook, so expanding a
            citations block (descendant-internal state) left the grown content unreachable.
          -->
          <div
            v-if="store.activeId && store.messages.length"
            ref="scrollLog"
            class="thread"
            @scroll="onThreadScroll"
          >
            <AiStudyChatLog
              ref="logRef"
              :messages="store.messages"
              :user-initials="userInitials"
              @retry="onRetry"
            />
          </div>

          <AiStudyChatEmpty
            v-else
            :mode="emptyMode"
            :suggestions="store.suggestions"
            :loading="store.isLoadingSuggestions"
            :scope="store.activeConversation?.scope ?? null"
            @new="isNewDialogVisible = true"
            @suggest="onSuggest"
          />

          <AiStudyChatComposer
            v-model="msg"
            :busy="store.isAwaitingReply"
            :disabled="quotaBlocked || !store.activeId"
            :context-label="contextLabel"
            @send="send"
          />
        </div>
      </div>
    </div>

    <NewConversationDialog
      v-model:is-dialog-visible="isNewDialogVisible"
      @started="onStarted"
    />

    <VSnackbar
      v-model="isSnackbarVisible"
      location="top end"
      color="error"
    >
      {{ snackbarText }}
    </VSnackbar>
  </div>
</template>

<style lang="scss">
@use '@/views/games/ai-study-chat/_tokens.scss';

.ai-study-chat-root {
  block-size: 100%;
  color: var(--text);
  // Breathing room for the panel's drop shadow — the layout gives the root
  // overflow-y:auto, which would otherwise clip the shadow at the root edge.
  padding: 12px;

  // Native <button> reset (prototype relies on it; the app doesn't style raw buttons).
  button { border: none; background: none; cursor: pointer; color: inherit; font-family: inherit; }

  .app-shell {
    display: grid;
    grid-template-rows: auto 1fr;
    block-size: 100%;
    min-block-size: 0;
    background: var(--bg);
    // Card look, consistent with the app's white cards: surface bg + hairline
    // border + drop shadow + rounded corners. overflow:hidden clips the topbar
    // and thread to the radius.
    border: 1px solid var(--border);
    border-radius: 16px;
    overflow: hidden;
    box-shadow: var(--shadow-md);
  }

  .topbar {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 22px;
    background: color-mix(in srgb, var(--surface) 72%, transparent);
    backdrop-filter: blur(12px);
    border-bottom: 1px solid var(--border);
  }

  .crumb {
    font-weight: 600;
    font-size: 14px;
    color: var(--text-2);
    display: flex;
    align-items: center;
    gap: 9px;
    min-width: 0;

    b { color: var(--text); font-weight: 600; }
    .sep { color: var(--text-3); }
    span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  }

  // Корень крошки — кнопка «домой» (welcome-экран с «Новой беседой»).
  .crumb-home {
    display: inline-flex;
    align-items: center;
    gap: 9px;
    font-size: inherit;
    font-weight: inherit;

    &:hover,
    &:hover b {
      color: var(--primary);
    }
  }

  // Тоггл мобильного drawer'а — виден только когда сайдбар ушёл в off-canvas.
  .sidebar-toggle {
    display: none;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    margin-left: -6px;
    border-radius: 9px;
    color: var(--text-2);
    flex: none;

    &:hover {
      background: var(--surface-2);
      color: var(--text);
    }
  }

  .workspace {
    display: grid;
    grid-template-columns: 312px 1fr;
    min-height: 0;
  }

  .chat {
    display: grid;
    // minmax(0, …): иначе 1fr не ужимается ниже min-content empty-состояния
    // и на низких экранах выталкивает композер за пределы viewport.
    grid-template-rows: minmax(0, 1fr) auto;
    min-height: 0;
    min-width: 0;
    position: relative;
  }

  .thread {
    overflow-y: auto;
    overscroll-behavior: contain;
    // Bottom padding matches the fade height below, so at the very bottom only the
    // empty padding sits in the fade zone — the last line stays crisp.
    padding: 28px 0 var(--thread-fade);
    min-height: 0;
    scroll-behavior: smooth;

    // Soft fade at the bottom edge so scrolled-past text dissolves into the bg
    // instead of being clipped sharply against the composer. The mask is anchored
    // to the element box (doesn't scroll), so it stays at the visible bottom edge.
    --thread-fade: 40px;
    mask-image: linear-gradient(to bottom, #000 calc(100% - var(--thread-fade)), transparent);
    -webkit-mask-image: linear-gradient(to bottom, #000 calc(100% - var(--thread-fade)), transparent);

    // Custom scrollbar, ported from the prototype (chat/styles.css:86-88).
    scrollbar-width: thin;
    scrollbar-color: var(--border-2) transparent;

    &::-webkit-scrollbar { width: 10px; height: 10px; }
    &::-webkit-scrollbar-thumb {
      background: var(--border-2); border-radius: 20px;
      border: 3px solid transparent; background-clip: content-box;
    }
    &::-webkit-scrollbar-thumb:hover { background: var(--text-3); background-clip: content-box; }
  }

  @media (max-width: 940px) {
    .workspace {
      grid-template-columns: 1fr;
      position: relative;
    }

    .sidebar-toggle { display: flex; }

    // Сайдбар уезжает в off-canvas drawer: позиционируется поверх чата,
    // выезжает по .sidebar-open. Фон делаем непрозрачным (в сетке он
    // полупрозрачный и подмешивает поверхность под собой).
    .convs {
      position: absolute;
      inset-block: 0;
      left: 0;
      width: min(312px, 85vw);
      z-index: 6;
      background: var(--surface);
      transform: translateX(-110%);
      transition: transform .25s cubic-bezier(.2, .8, .3, 1);
    }

    .workspace.sidebar-open .convs {
      transform: translateX(0);
      box-shadow: var(--shadow-lg);
    }

    .sidebar-scrim {
      position: absolute;
      inset: 0;
      z-index: 5;
      background: var(--scrim);
      animation: chat-scrim-fade .2s;
    }
  }
}

@keyframes chat-scrim-fade { from { opacity: 0; } to { opacity: 1; } }

@media (prefers-reduced-motion: reduce) {
  .ai-study-chat-root *,
  .ai-study-chat-root *::before,
  .ai-study-chat-root *::after {
    animation-duration: .01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: .01ms !important;
  }
}
</style>
